import React from "react";

import {
  formatFractionDisplay,
  formatNumber,
  getMidpointGrowingDate,
  useGetFarmFieldListAtPointInTime,
  useGetPracticeCrops,
  useGetPracticeMineralFertilizers,
  useGetPracticeOrganicFertilizers,
  useGetPracticePlantProtections,
  useGetPracticeSeedings,
  useGetPracticeTillages,
  useParams,
  useTranslation,
} from "utils";

import { CropFootprintReturnBody, PRACTICE_SLUG } from "models";

import { Card, ListCard, PageSpin } from "components";

import { ReviewPageTitle } from "pages/app/footprinting/components/ReviewPageTitle";
import { CropFertilizersForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropFertilizersForm";
import { CropFieldSelectionForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropFieldSelectionForm";
import { CropGeneralForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropGeneralForm";
import { CropIrrigationForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropIrrigationForm";
import { CropPlantProtectionForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropPlantProtectionForm";
import { CropSeedingForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropSeedingForm";
import { CropTillageForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropTillageForm";
import { CropTransportForm } from "pages/app/footprinting/components/formFlows/cropFlow/CropTransportForm";

export const FootprintEditCropDetailsCard = ({
  cropFootprintData,
  isCropFootprintDataLoading,
  setShowEditModal,
  setCropModalForm,
}: {
  cropFootprintData: CropFootprintReturnBody;
  isCropFootprintDataLoading: boolean;
  setShowEditModal: (show: boolean) => void;
  setCropModalForm: (formType: string) => void;
}) => {
  const { t } = useTranslation(["footprintingCrop", "practices"]);
  const { farmId } = useParams();
  const allCropsQuery = useGetPracticeCrops({ enabled: true, isAdmin: true });
  const midpointDateForYear = getMidpointGrowingDate(allCropsQuery.data?.find((c) => c.id === cropFootprintData.cropId), cropFootprintData.year);

  const tillagePracticesQuery = useGetPracticeTillages({ enabled: true });
  const seedingPracticesQuery = useGetPracticeSeedings({ enabled: true });
  const mineralFertilizerPracticesQuery = useGetPracticeMineralFertilizers({ enabled: true });
  const organicFertilizerPracticesQuery = useGetPracticeOrganicFertilizers({ enabled: true });
  const plantProtectionPracticesQuery = useGetPracticePlantProtections();

  const fieldsQuery = useGetFarmFieldListAtPointInTime({
    farmId,
    date: midpointDateForYear?.toISOString() ?? "",
    enabled: !!midpointDateForYear,
  });

  const displayRadioAnswer = (isActive: boolean | null) => (isActive === null ? "" : t(`footprinting:radioAnswer.${isActive}`));

  if (isCropFootprintDataLoading) return <PageSpin />;

  // Verified footprints are read-only: the admin unverify is the only write the API accepts
  const editButton = (formKey: string) =>
    cropFootprintData.footprint?.isVerified
      ? undefined
      : {
          label: t("footprinting:review.edit"),
          onClick: () => {
            setShowEditModal(true);
            setCropModalForm(formKey);
          },
        };

  return (
    <Card className="admin-edit-crop-details-card padding-125">
      <ReviewPageTitle
        title={`${t("footprinting:review.crop.title", { cropName: allCropsQuery.data?.find((c) => c.id === cropFootprintData.cropId)?.name })} - ${
          cropFootprintData.year
        }`}
        iconName="klim/crops-matured"
      />
      {/* Crop general form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        title={t("footprinting:navigation.crop.general")}
        titleButton={editButton(CropGeneralForm.Key)}
        dataPairs={[
          {
            label: t("cropGeneralForm.question.totalWeight"),
            value: `${cropFootprintData.footprint?.totalWeightPerHa} ${cropFootprintData.footprint?.totalWeightPerHaUnit}`,
          },
          {
            label: t("cropGeneralForm.question.preDryingMoistureContentPct"),
            value: `${formatFractionDisplay(cropFootprintData.footprint?.preDryingMoistureContentPct ?? 0)} %`,
          },
          {
            label: "Normalized weight at commercial moisture content",
            value: `${formatNumber({ number: cropFootprintData?.footprint?.normalizedWeightTs ?? 0, locale: "en-US" })} t`,
          },
          { label: t("cropGeneralForm.question.byproductPct"), value: `${formatFractionDisplay(cropFootprintData?.footprint?.byproductPct ?? 0)} %` },
          { label: t("cropGeneralForm.question.seedWeightKgPerHa"), value: `${cropFootprintData?.footprint?.seedWeightKgPerHa} kg/ha` },
          { label: t("cropGeneralForm.question.soilPh"), value: `${cropFootprintData?.footprint?.soilPh} pH` },
        ]}
      />
      {/* Crop fields form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        title={t("footprinting:navigation.crop.fields")}
        titleButton={editButton(CropFieldSelectionForm.Key)}
        dataPairs={[
          {
            label: "Total hectares",
            value: `${formatNumber({ number: cropFootprintData.footprint?.totalFieldHectares ?? 0, locale: "en-US" })}`,
          },
          ...(cropFootprintData?.footprint?.fieldIds ?? []).map((fieldId) => {
            const field = fieldsQuery.data?.find((f) => f.id === fieldId);

            if (!field) return { label: "Broken field ref", value: "Don't worry, no calculation effect" };

            return { label: field?.name ?? "", value: `${field?.size ?? 0} ha` };
          }),
        ]}
      />
      {/* Crop tillage form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        title={t("footprinting:navigation.crop.tillage")}
        titleButton={editButton(CropTillageForm.Key)}
        dataPairs={[
          {
            label: t("cropTillageForm.tillage.question"),
            value: displayRadioAnswer(cropFootprintData?.footprint?.tillageActive ?? null),
            children: cropFootprintData?.footprint?.tillageActive
              ? cropFootprintData?.footprint?.tillageUsage.map((i, index) => (
                  <ListCard
                    key={index}
                    isSublist
                    title={t("cropTillageForm.tillage.count") + (index + 1)}
                    dataPairs={[
                      {
                        label: t("cropTillageForm.tillage.type"),
                        value: t(`${PRACTICE_SLUG.TILLAGES}.${tillagePracticesQuery.data?.find((p) => p.id === i.applicationId)?.translationSlug}`, {
                          ns: "practices",
                        }),
                      },
                      {
                        label: t("cropTillageForm.tillage.depth"),
                        value: `${i.tillageDepth} cm`,
                      },
                      {
                        label: t("cropTillageForm.question.implementedOn"),
                        value: `${i.implementedOnHa} ha`,
                      },
                    ]}
                  />
                ))
              : null,
          },
        ]}
      />
      {/* Crop seeding form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        title={t("footprinting:navigation.crop.seeding")}
        titleButton={editButton(CropSeedingForm.Key)}
        dataPairs={[
          {
            label: t("cropSeedingForm.seeding.question"),
            value: displayRadioAnswer(cropFootprintData?.footprint?.seedingActive ?? null),
            children: cropFootprintData?.footprint?.seedingActive
              ? cropFootprintData?.footprint?.seedingUsage.map((i, index) => (
                  <ListCard
                    key={index}
                    isSublist
                    title={t("cropSeedingForm.seeding.count") + (index + 1)}
                    dataPairs={[
                      {
                        label: t("cropSeedingForm.seeding.type"),
                        value: t(`${PRACTICE_SLUG.SEEDINGS}.${seedingPracticesQuery.data?.find((p) => p.id === i.applicationId)?.translationSlug}`, {
                          ns: "practices",
                        }),
                      },
                      {
                        label: t("cropSeedingForm.question.implementedOn"),
                        value: `${i.implementedOnHa} ha`,
                      },
                    ]}
                  />
                ))
              : null,
          },
        ]}
      />
      {/* Crop fertilizers form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        title={t("footprinting:navigation.crop.fertilizers")}
        titleButton={editButton(CropFertilizersForm.Key)}
        dataPairs={[
          {
            label: t("cropFertilizerForm.synthetic.question"),
            value: displayRadioAnswer(cropFootprintData?.footprint?.mineralFertilizerActive ?? null),
            children: cropFootprintData?.footprint?.mineralFertilizerActive
              ? cropFootprintData?.footprint?.mineralFertilizerUsage.map((i, index) => (
                  <ListCard
                    key={index}
                    isSublist
                    title={t("cropFertilizerForm.application.title") + (index + 1)}
                    dataPairs={[
                      {
                        label: t("cropFertilizerForm.application.productName"),
                        value: t(
                          `${PRACTICE_SLUG.MINERAL_FERTILIZERS}.${mineralFertilizerPracticesQuery.data?.find((p) => p.id === i.applicationId)
                            ?.translationSlug}`,
                          { ns: "practices" }
                        ),
                      },
                      {
                        label: t("cropFertilizerForm.application.amount"),
                        value: `${i.amount} ${i.amountUnit}`,
                      },
                    ]}
                  />
                ))
              : null,
          },
          {
            label: t("cropFertilizerForm.organic.question"),
            value: displayRadioAnswer(cropFootprintData?.footprint?.organicFertilizerActive ?? null),
            children: cropFootprintData?.footprint?.organicFertilizerActive
              ? cropFootprintData?.footprint?.organicFertilizerUsage.map((i, index) => (
                  <ListCard
                    key={index}
                    isSublist
                    title={t("cropFertilizerForm.application.title") + (index + 1)}
                    dataPairs={[
                      {
                        label: t("cropFertilizerForm.application.productName"),
                        value: t(
                          `${PRACTICE_SLUG.ORGANIC_FERTILIZERS}.${organicFertilizerPracticesQuery.data?.find((p) => p.id === i.applicationId)
                            ?.translationSlug}`,
                          { ns: "practices" }
                        ),
                      },
                      {
                        label: t("cropFertilizerForm.application.nitrogen"),
                        value: `${i.nValue}`,
                      },
                      {
                        label: t("cropFertilizerForm.application.amount"),
                        value: `${i.amount} ${i.amountUnit}`,
                      },
                    ]}
                  />
                ))
              : null,
          },
          { label: t("cropFertilizerForm.application.lime"), value: `${cropFootprintData?.footprint?.limeT} tonnes` },
        ]}
      />
      {/* Crop plant protection form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        title={t("footprinting:navigation.crop.plantProtection")}
        titleButton={editButton(CropPlantProtectionForm.Key)}
        dataPairs={[
          {
            label: t("cropPlantProtectionForm.question.plantProtectionQuestionLabel"),
            value: displayRadioAnswer(cropFootprintData?.footprint?.plantProtectionActive ?? null),
            children: cropFootprintData?.footprint?.plantProtectionActive
              ? cropFootprintData?.footprint?.plantProtectionUsage.map((i, index) => (
                  <ListCard
                    key={index}
                    isSublist
                    title={t("cropPlantProtectionForm.question.applicationNumber", { count: index + 1 })}
                    dataPairs={[
                      {
                        label: t("cropPlantProtectionForm.question.plantProtectionTypeQuestionLabel"),
                        value: t(
                          `${PRACTICE_SLUG.PLANT_PROTECTIONS}.${plantProtectionPracticesQuery.data?.find((p) => p.id === i.applicationId)?.translationSlug}`,
                          { ns: "practices" }
                        ),
                      },
                      {
                        label: t("cropPlantProtectionForm.question.amountAppliedQuestionLabel"),
                        value: `${i.amount} ${i.amountUnit}`,
                      },
                      {
                        label: t("cropPlantProtectionForm.question.activeIngredientsQuestionLabel"),
                        value: `${formatFractionDisplay(i.activeIngredientsPct ?? 0)} %`,
                      },
                    ]}
                  />
                ))
              : null,
          },
        ]}
      />
      {/* Crop irrigation form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        title={t("footprinting:navigation.crop.irrigation")}
        titleButton={editButton(CropIrrigationForm.Key)}
        dataPairs={[
          {
            label: t("cropIrrigation.question.irrigation"),
            value: displayRadioAnswer(cropFootprintData?.footprint?.irrigationActive ?? null),
          },
          ...(cropFootprintData?.footprint?.irrigationActive
            ? [
                {
                  label: t("cropIrrigation.question.waterUsedQuestionLabel"),
                  value: `${cropFootprintData?.footprint?.waterUsedM3} m³`,
                },
              ]
            : []),
        ]}
      />
      {/* Crop transport form */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        title={t("footprinting:navigation.crop.transport")}
        titleButton={editButton(CropTransportForm.Key)}
        dataPairs={[
          {
            label: t("cropTransportForm.question.radioQuestion"),
            value: displayRadioAnswer(cropFootprintData?.footprint?.cropSaleActive ?? null),
            children: cropFootprintData?.footprint?.cropSaleActive
              ? cropFootprintData?.footprint?.cropSales.map((i, index) => (
                  <ListCard
                    key={index}
                    isSublist
                    title={t("farmTransportForm.retailer", { count: index + 1 })}
                    dataPairs={[
                      {
                        label: t("cropTransportForm.question.purchaserNameQuestionLabel"),
                        value: i.purchaserName ?? "",
                      },
                      {
                        label: t("cropTransportForm.question.countryQuestionLabel"),
                        value: i.purchaserCountryCode ?? "",
                      },
                      {
                        label: t("cropTransportForm.question.postcodeQuestionLabel"),
                        value: i.purchaserPostCode ?? "",
                      },
                      {
                        label: t("cropTransportForm.question.shareQuestionLabel"),
                        value: `${i.soldYieldTs} t`,
                      },
                    ]}
                  />
                ))
              : null,
          },
        ]}
      />
      {/* Comments */}
      <ListCard
        className="margin-bottom-100"
        isOutlined
        isLoading={isCropFootprintDataLoading}
        title={t("commentForm.title")}
        dataPairs={[
          {
            label: t("commentForm.label"),
            value: cropFootprintData?.footprint?.comment || t("commentForm.noComment"),
          },
        ]}
      />
    </Card>
  );
};
